import React, { useEffect } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    Typography,
    FormControl,
    FormHelperText,
    Box,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    Paper,
} from '@mui/material';
import { StepWrapper, StyledCardSection, StepperControls, TextField, DatePicker } from '@fnol/library/src/components';
import {
    GenericObject,
    common,
    formatDate,
    formatDateSrb,
    maxCharacterLimit,
    checkIfSerbianPhoneNumber,
    ScreenProps,
} from '@fnol/library/src/lib';
import { riskTypeLife, translations, questionMaxLength, personalDocumentOptions } from '../../lib/consts';
import { getUsersList } from '../../rpc/actions';
import { useApi } from '@fnol/library/src/hooks';
import { getCurrentDate } from '../../lib/helpers';
import svgChecked from '../../assets/images/checked.svg';
import svgError from '../../assets/images/error.svg';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const ClaimDetailScreen = ({ navigator }: ScreenProps) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, errors, touched, setErrors, setTouched, setFieldValue } = useFormikContext<GenericObject>();

    const hospitalDaysRequired = () => {
        return values?.risks.some((risk: any) =>
            [riskTypeLife.hospitalDaysDueToIllness, riskTypeLife.hospitalDaysDueToInjury].includes(risk),
        );
    };

    const saveUsersList = (res: GenericObject) => {
        if (!res.data[0].poruka || res.data[0].poruka === '') {
            setFieldValue('usersList', res.data);
        }
    };

    const { response: usersList, fetchMethod: fetchUsersList } = useApi(
        () => getUsersList({ BrojStete: values.claimCode }),
        saveUsersList,
    );

    useEffect(() => {
        values.risks.includes(riskTypeLife.outlive) && fetchUsersList();
    }, []);

    useEffect(() => {
        if (values.usersList?.length > 0 && !values.risks.includes(riskTypeLife.purchase)) {
            const getValue = (editValue: string, userValue: string) => {
                return !editValue ? userValue : editValue;
            };
            for (let i = 0; i < values.usersList.length; i++) {
                const validCity = values.cities.find((city: GenericObject) => city.label === values.usersList[i].mesto);
                const editId = i + 1;
                setFieldValue(`user${editId}Name`, getValue(values[`user${editId}Name`], values.usersList[i].ime));
                setFieldValue(
                    `user${editId}LastName`,
                    getValue(values[`user${editId}LastName`], values.usersList[i].prezime),
                );
                setFieldValue(
                    `user${editId}DateOfBirth`,
                    getValue(values[`user${editId}DateOfBirth`], values.usersList[i].datRodj),
                );
                setFieldValue(`user${editId}UCN`, getValue(values[`user${editId}UCN`], values.usersList[i].jmbg));
                setFieldValue(
                    `user${editId}Citizenship`,
                    getValue(values[`user${editId}Citizenship`], values.usersList[i].drzavljanstvo),
                );
                setFieldValue(
                    `user${editId}DualCitizenship`,
                    getValue(values[`user${editId}DualCitizenship`], values.usersList[i].drzavljanstvo2),
                );
                setFieldValue(
                    `user${editId}Country`,
                    getValue(values[`user${editId}Country`], values.usersList[i].drzava),
                );
                setFieldValue(
                    `user${editId}MyCountry`,
                    getValue(values[`user${editId}MyCountry`], values.usersList[i].drzava),
                );
                setFieldValue(`user${editId}City`, getValue(values[`user${editId}City`], values.usersList[i].mesto));
                setFieldValue(`user${editId}CityInSerbia`, getValue(values[`user${editId}CityInSerbia`], validCity));
                setFieldValue(
                    `user${editId}Municipality`,
                    getValue(values[`user${editId}Municipality`], values.usersList[i].opstinaID),
                );
                setFieldValue(
                    `user${editId}Street`,
                    getValue(values[`user${editId}Street`], values.usersList[i].adresa),
                );
                const userListPhoneNumber = checkIfSerbianPhoneNumber(values.usersList[i].telefon)
                    ? values.usersList[i].telefon
                    : '';
                setFieldValue(`user${editId}Phone`, getValue(values[`user${editId}Phone`], userListPhoneNumber));
                setFieldValue(`user${editId}Email`, getValue(values[`user${editId}Email`], values.usersList[i].email));
                setFieldValue(
                    `user${editId}PersonalDocumentType`,
                    getValue(values[`user${editId}PersonalDocumentType`], values.usersList[i].vrstaDokumenta),
                );
                setFieldValue(
                    `user${editId}PersonalDocumentNumber`,
                    getValue(values[`user${editId}PersonalDocumentNumber`], values.usersList[i].brojLicnogDokumenta),
                );
                setFieldValue(
                    `user${editId}DocumentIssuePlace`,
                    getValue(values[`user${editId}DocumentIssuePlace`], values.usersList[i].mestoIzdavanjaLDokumenta),
                );
                setFieldValue(
                    `user${editId}DocumentIssueDate`,
                    getValue(values[`user${editId}DocumentIssueDate`], values.usersList[i].datumIzdavanjaLDokumenta),
                );
                setFieldValue(
                    `user${editId}PlaceOfBirth`,
                    getValue(values[`user${editId}PlaceOfBirth`], values.usersList[i].mestoRodjenja),
                );
                setFieldValue(`user${editId}Gender`, getValue(values[`user${editId}Gender`], values.usersList[i].pol));
                setFieldValue(
                    `user${editId}CurrentResidenceAddress`,
                    getValue(
                        values[`user${editId}CurrentResidenceAddress`],
                        values.usersList[i].istaAdresaPrebivalista,
                    ),
                );
                setFieldValue(
                    `user${editId}CurrentResidenceStreet`,
                    getValue(values[`user${editId}CurrentResidenceStreet`], values.usersList[i].adresaPrebivalista),
                );
                setFieldValue(
                    `user${editId}CurrentResidenceCity`,
                    getValue(values[`user${editId}CurrentResidenceCity`], values.usersList[i].mestoPrebivalista),
                );
                setFieldValue(
                    `user${editId}CurrentResidenceCityInSerbia`,
                    getValue(
                        values[`user${editId}CurrentResidenceCityInSerbia`],
                        values.usersList[i].mestoPrebivalista,
                    ),
                );
                setFieldValue(
                    `user${editId}MailingAddress`,
                    getValue(values[`user${editId}MailingAddress`], values.usersList[i].istaAdresaZaPostu),
                );
                setFieldValue(
                    `user${editId}MailingStreet`,
                    getValue(values[`user${editId}MailingStreet`], values.usersList[i].adresaZaPostu),
                );
                setFieldValue(
                    `user${editId}MailingCity`,
                    getValue(values[`user${editId}MailingCity`], values.usersList[i].mestoZaPostu),
                );
                setFieldValue(
                    `user${editId}MailingCityInSerbia`,
                    getValue(values[`user${editId}MailingCityInSerbia`], values.usersList[i].mestoZaPostu),
                );
                setFieldValue(
                    `user${editId}isSubmitterUser`,
                    getValue(values[`user${editId}isSubmitterUser`], values.usersList[i].podnPrijave),
                );
                setFieldValue(`user${editId}ZipCode`, getValue(values[`user${editId}ZipCode`], values.userZipCode));

                setFieldValue(
                    `user${editId}CurrentResidenceZipCode`,
                    getValue(values[`user${editId}CurrentResidenceZipCode`], values.userCurrentResidenceZipCode),
                );
                setFieldValue(
                    `user${editId}CurrentResidenceMunicipality`,
                    getValue(
                        values[`user${editId}CurrentResidenceMunicipality`],
                        values.userCurrentResidenceMunicipality,
                    ),
                );
                setFieldValue(
                    `user${editId}MailingMunicipality`,
                    getValue(values[`user${editId}MailingMunicipality`], values.userMailingMunicipality),
                );
                setFieldValue(
                    `user${editId}MailingZipCode`,
                    getValue(values[`user${editId}MailingZipCode`], values.userMailingZipCode),
                );
                setFieldValue(
                    `user${editId}AttorneySignature`,
                    getValue(values[`user${editId}AttorneySignature`], values.userAttorneySignature),
                );

                setFieldValue(
                    `user${editId}Statement`,
                    getValue(values[`user${editId}Statement`], values.userStatement),
                );
                setFieldValue(
                    `user${editId}StatementNote`,
                    getValue(values[`user${editId}StatementNote`], values.userStatementNote),
                );
            }
        }
    }, [values.usersList]);

    useEffect(() => {
        if (values.risks.includes(riskTypeLife.purchase) && values.contractorData) {
            setFieldValue(`userTypePurchase`, values.contractorData?.ugovaracVrstaLica);
            const getValue = (editValue: string, userValue: string) => {
                return !editValue ? userValue : editValue;
            };
            if (values.contractorData?.ugovaracVrstaLica === 1 && values.contractorData.ugovaracFL) {
                const validCity = values.cities.find(
                    (city: GenericObject) => city.label === values.contractorData.ugovaracFL.mesto,
                );
                const docType = personalDocumentOptions.filter(
                    (doc: GenericObject) => doc.id === values.contractorData.ugovaracFL.licniDokument,
                )[0]?.id;

                const citizenship = values.citizenships?.filter(
                    (citizen: GenericObject) => citizen.id === values.contractorData.ugovaracFL?.drzavljanstvoId,
                )[0]?.value;
                const dualCitizenship = values.citizenships?.filter(
                    (citizen: GenericObject) => citizen.id === values.contractorData.ugovaracFL?.drugoDrzavljanstvoId,
                )[0]?.value;

                setFieldValue(`userName`, getValue(values[`userName`], values.contractorData.ugovaracFL.ime));
                setFieldValue(
                    `userLastName`,
                    getValue(values[`userLastName`], values.contractorData.ugovaracFL.prezime),
                );
                setFieldValue(
                    `userDateOfBirth`,
                    getValue(
                        values[`userDateOfBirth`],
                        new Date(values.contractorData.ugovaracFL.datumRodjenja).toString(),
                    ),
                );
                setFieldValue(`userUCN`, getValue(values[`userUCN`], values.contractorData.ugovaracFL.jmbg));
                setFieldValue(`userCitizenship`, getValue(values[`userCitizenship`], citizenship));
                setFieldValue(`userDualCitizenship`, getValue(values[`userDualCitizenship`], dualCitizenship));
                setFieldValue(
                    `userCountry`,
                    getValue(values[`userCountry`], values.contractorData.ugovaracFL.drzavaId),
                );
                setFieldValue(
                    `userMyCountry`,
                    getValue(values[`userMyCountry`], values.contractorData.ugovaracFL.drzavaId),
                );
                setFieldValue(`userCity`, getValue(values[`userCity`], values.contractorData.ugovaracFL.mesto));
                setFieldValue(`userCityInSerbia`, getValue(values[`userCityInSerbia`], validCity));
                setFieldValue(
                    `userMunicipality`,
                    getValue(values[`userMunicipality`], values.contractorData.ugovaracFL.opstinaID),
                );
                setFieldValue(`userStreet`, getValue(values[`userStreet`], values.contractorData.ugovaracFL.adresa));
                const userListPhoneNumber = checkIfSerbianPhoneNumber(values.contractorData.ugovaracFL.mobilniTelefon)
                    ? values.contractorData.ugovaracFL.mobilniTelefon
                    : '';

                const phoneNumberValue =
                    values['userPhone'] === '+381'
                        ? userListPhoneNumber === ''
                            ? values['userPhone']
                            : userListPhoneNumber
                        : values['userPhone'];

                setFieldValue(`userPhone`, phoneNumberValue);
                setFieldValue(`userEmail`, getValue(values[`userEmail`], values.contractorData.ugovaracFL.email));
                setFieldValue(
                    `userPersonalDocumentType`,
                    getValue(values[`userPersonalDocumentType`], docType?.toString()),
                );
                setFieldValue(
                    `userPersonalDocumentNumber`,
                    getValue(
                        values[`userPersonalDocumentNumber`],
                        values.contractorData.ugovaracFL.brojLicnogDokumenta,
                    ),
                );
                setFieldValue(
                    `userDocumentIssuePlace`,
                    getValue(
                        values[`userDocumentIssuePlace`],
                        values.contractorData.ugovaracFL.mestoIzdavanjaLDokumenta,
                    ),
                );
                setFieldValue(
                    `userDocumentIssueDate`,
                    getValue(
                        values[`userDocumentIssueDate`],
                        new Date(values.contractorData.ugovaracFL.datumIzdavanjaLDokumenta).toString(),
                    ),
                );
                setFieldValue(
                    `userPlaceOfBirth`,
                    getValue(values[`userPlaceOfBirth`], values.contractorData.ugovaracFL.mestoRodjenja),
                );
                setFieldValue(`userGender`, getValue(values[`userGender`], values.contractorData.ugovaracFL.pol));
                setFieldValue(
                    `userCurrentResidenceAddress`,
                    getValue(
                        values[`userCurrentResidenceAddress`],
                        values.contractorData.ugovaracFL.istaAdresaPrebivalista,
                    ),
                );
                setFieldValue(
                    `userCurrentResidenceStreet`,
                    getValue(values[`userCurrentResidenceStreet`], values.contractorData.ugovaracFL.adresaPrebivalista),
                );
                setFieldValue(
                    `userCurrentResidenceCity`,
                    getValue(values[`userCurrentResidenceCity`], values.contractorData.ugovaracFL.mestoPrebivalista),
                );
                setFieldValue(
                    `userCurrentResidenceCityInSerbia`,
                    getValue(
                        values[`userCurrentResidenceCityInSerbia`],
                        values.contractorData.ugovaracFL.mestoPrebivalista,
                    ),
                );
                setFieldValue(
                    `userMailingAddress`,
                    getValue(values[`userMailingAddress`], values.contractorData.ugovaracFL.istaAdresaZaPostu),
                );
                setFieldValue(
                    `userMailingStreet`,
                    getValue(values[`userMailingStreet`], values.contractorData.ugovaracFL.adresaZaPostu),
                );
                setFieldValue(
                    `userMailingCity`,
                    getValue(values[`userMailingCity`], values.contractorData.ugovaracFL.mestoZaPostu),
                );
                setFieldValue(
                    `userMailingCityInSerbia`,
                    getValue(values[`userMailingCityInSerbia`], values.contractorData.ugovaracFL.mestoZaPostu),
                );
                setFieldValue(
                    `userisSubmitterUser`,
                    getValue(values[`userisSubmitterUser`], values.contractorData.ugovaracFL.podnPrijave),
                );
                setFieldValue(`userZipCode`, getValue(values[`userZipCode`], values.userZipCode));

                setFieldValue(
                    `userCurrentResidenceZipCode`,
                    getValue(values[`userCurrentResidenceZipCode`], values.userCurrentResidenceZipCode),
                );
                setFieldValue(
                    `userCurrentResidenceMunicipality`,
                    getValue(values[`userCurrentResidenceMunicipality`], values.userCurrentResidenceMunicipality),
                );
                setFieldValue(
                    `userMailingMunicipality`,
                    getValue(values[`userMailingMunicipality`], values.userMailingMunicipality),
                );
                setFieldValue(`userMailingZipCode`, getValue(values[`userMailingZipCode`], values.userMailingZipCode));
                setFieldValue(
                    `userAttorneySignature`,
                    getValue(values[`userAttorneySignature`], values.userAttorneySignature),
                );

                setFieldValue(`userStatement`, getValue(values[`userStatement`], values.userStatement));
                setFieldValue(`userStatementNote`, getValue(values[`userStatementNote`], values.userStatementNote));
            }

            if (values.contractorData?.ugovaracVrstaLica === 2 && values.contractorData.ugovaracPL) {
                const validCity = values.cities.find((city: GenericObject) => {
                    city.label === values.contractorData.ugovaracPL?.mesto;
                });

                const docType = personalDocumentOptions?.filter(
                    (doc: GenericObject) =>
                        doc.id === values.contractorData.ugovaracPL?.licniDokumentZakonskogZastupnika,
                )[0]?.id;

                setFieldValue(
                    `userBusinessName`,
                    getValue(values[`userBusinessName`], values.contractorData.ugovaracPL.naziv),
                );
                setFieldValue(`userVAT`, getValue(values[`userVAT`], values.contractorData.ugovaracPL.pib));
                setFieldValue(
                    `userName`,
                    getValue(values[`userName`], values.contractorData.ugovaracPL.imeZakonskogZastupnika),
                );
                setFieldValue(
                    `userLastName`,
                    getValue(values[`userLastName`], values.contractorData.ugovaracPL.prezimeZakonskogZastupnika),
                );
                setFieldValue(
                    `userEstablishmentDate`,
                    getValue(
                        values[`userEstablishmentDate`],
                        values.contractorData.ugovaracPL.datumOsnivanja
                            ? new Date(values.contractorData.ugovaracPL.datumOsnivanja).toString()
                            : '',
                    ),
                );
                setFieldValue(
                    `userEstablishmentPlace`,
                    getValue(values[`userEstablishmentPlace`], values.contractorData.ugovaracPL.mestoOsnivanja),
                );
                setFieldValue(
                    `userCountry`,
                    getValue(values[`userCountry`], values.contractorData.ugovaracPL.drzavaId),
                );
                setFieldValue(
                    `userMyCountry`,
                    getValue(values[`userMyCountry`], values.contractorData.ugovaracPL.drzavaId),
                );
                setFieldValue(`userCity`, getValue(values[`userCity`], values.contractorData.ugovaracPL?.mesto));
                setFieldValue(
                    `userCityInSerbia`,
                    getValue(values[`userCityInSerbia`], values.contractorData.ugovaracPL?.mesto),
                );
                setFieldValue(
                    `userMunicipality`,
                    getValue(values[`userMunicipality`], values.contractorData.ugovaracPL.opstina),
                );
                setFieldValue(`userStreet`, getValue(values[`userStreet`], values.contractorData.ugovaracPL.ulicaBroj));
                const userListPhoneNumber = checkIfSerbianPhoneNumber(
                    values.contractorData.ugovaracPL.kontaktMobilniTelefon,
                )
                    ? values.contractorData.ugovaracPL.kontaktMobilniTelefon
                    : '';

                const phoneNumberValue =
                    values['userPhone'] === '+381'
                        ? userListPhoneNumber === ''
                            ? values['userPhone']
                            : userListPhoneNumber
                        : values['userPhone'];

                setFieldValue(`userPhone`, phoneNumberValue);
                setFieldValue(`userEmail`, getValue(values[`userEmail`], values.contractorData.ugovaracPL.email));
                setFieldValue(
                    `userPersonalDocumentType`,
                    getValue(values[`userPersonalDocumentType`], docType?.toString()),
                );
                setFieldValue(
                    `userPersonalDocumentNumber`,
                    getValue(
                        values[`userPersonalDocumentNumber`],
                        values.contractorData.ugovaracPL.brojLicnogDokumentaZakonskogZastupnika,
                    ),
                );
                setFieldValue(
                    `userDocumentIssuePlace`,
                    getValue(
                        values[`userDocumentIssuePlace`],
                        values.contractorData.ugovaracPL.mestoIzdavanjaLicnogDokumentaZakonskogZastupnika,
                    ),
                );
                setFieldValue(
                    `userDocumentIssueDate`,
                    getValue(
                        values[`userDocumentIssueDate`],
                        values.contractorData.ugovaracPL.datumIzdavanjaLicnogDokumentaZakonskogZastupnika
                            ? new Date(
                                  values.contractorData.ugovaracPL.datumIzdavanjaLicnogDokumentaZakonskogZastupnika,
                              ).toString()
                            : '',
                    ),
                );
                setFieldValue(
                    `userGender`,
                    getValue(values[`userGender`], values.contractorData.ugovaracPL.polZakonskogZastupnika),
                );
                setFieldValue(
                    `userZipCode`,
                    getValue(values[`userZipCode`], values.contractorData.ugovaracPL.postanskiBroj),
                );
                setFieldValue(
                    `userAttorneySignature`,
                    getValue(values[`userAttorneySignature`], values.userAttorneySignature),
                );
            }
        }
    }, []);

    const firstCondition =
        values.risks.includes(riskTypeLife.injury) ||
        values.risks.includes(riskTypeLife.hospitalDaysDueToInjury) ||
        values.risks.includes(riskTypeLife.surgicalInterventionDueToInjury);
    const secondCondition =
        values.risks.includes(riskTypeLife.deathByAccident) ||
        values.risks.includes(riskTypeLife.deathByTrafficAccident);

    const handleNext = (): void => {
        let valid = true;
        const injury = {
            description: true,
            typeOfInjury: true,
            surgicalInvestigation: true,
            hospitalDaysStart: true,
            hospitalDaysEnd: true,
            investigation: true,
            firstAid: true,
            physicalFlaw: true,
            sameInjury: true,
            prohibitedSubstances: true,
        };
        const hospitalDaysDueToIllness = {
            cause: true,
            hospitalDaysStart: true,
            hospitalDaysEnd: true,
            hospitalType: true,
            firstContactDate: true,
            medicalRecord: true,
        };
        const surgicalInterventionDueToIllness = {
            diagnosis: true,
            surgicalIntervention: true,
            hospitalOfSurgical: true,
            dateOfFirstContact: true,
            medicalRecord: true,
        };
        const death = {
            deathCause: true,
            drName: true,
            institutionName: true,
        };
        const deathByAccident = {
            description: true,
            firstAid: true,
            investigation: true,
            prohibitedSubstances: true,
        };
        const seriousIllness = {
            dateOfSeriousIllness: true,
            diagnosisSeriousIllness: true,
            seriousIllnessTreatment: true,
            medicalRecord: true,
        };
        const touched = firstCondition
            ? { ...injury }
            : values.risks.includes(riskTypeLife.hospitalDaysDueToIllness)
            ? { ...hospitalDaysDueToIllness }
            : values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness)
            ? { ...surgicalInterventionDueToIllness }
            : values.risks.includes(riskTypeLife.death)
            ? { ...death }
            : secondCondition
            ? { ...deathByAccident }
            : values.risks.includes(riskTypeLife.seriousIllness)
            ? { ...seriousIllness }
            : {};

        setTouched(touched, false);

        const existingErrors: GenericObject = {};
        if (!values.description && (firstCondition || secondCondition)) {
            existingErrors.description = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.typeOfInjury && firstCondition) {
            existingErrors.typeOfInjury = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.surgicalInvestigation && firstCondition) {
            existingErrors.surgicalInvestigation = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (hospitalDaysRequired() && !values.hospitalDaysStart) {
            existingErrors.hospitalDaysStart = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (hospitalDaysRequired() && !values.hospitalDaysEnd) {
            existingErrors.hospitalDaysEnd = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.investigation && (firstCondition || secondCondition)) {
            existingErrors.investigation = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.firstAid && (firstCondition || secondCondition)) {
            existingErrors.firstAid = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.physicalFlaw && firstCondition) {
            existingErrors.physicalFlaw = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.sameInjury && firstCondition) {
            existingErrors.sameInjury = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.prohibitedSubstances && (firstCondition || secondCondition)) {
            existingErrors.prohibitedSubstances = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.cause && values.risks.includes(riskTypeLife.hospitalDaysDueToIllness)) {
            existingErrors.cause = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.hospitalType && values.risks.includes(riskTypeLife.hospitalDaysDueToIllness)) {
            existingErrors.hospitalType = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.firstContactDate && values.risks.includes(riskTypeLife.hospitalDaysDueToIllness)) {
            existingErrors.firstContactDate = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (
            !values.medicalRecord &&
            (values.risks.includes(riskTypeLife.hospitalDaysDueToIllness) ||
                values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness) ||
                values.risks.includes(riskTypeLife.seriousIllness))
        ) {
            existingErrors.medicalRecord = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.diagnosis && values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness)) {
            existingErrors.diagnosis = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.surgicalIntervention && values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness)) {
            existingErrors.surgicalIntervention = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.hospitalOfSurgical && values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness)) {
            existingErrors.hospitalOfSurgical = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.dateOfFirstContact && values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness)) {
            existingErrors.dateOfFirstContact = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.deathCause && values.risks.includes(riskTypeLife.death)) {
            existingErrors.deathCause = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.drName && values.risks.includes(riskTypeLife.death)) {
            existingErrors.drName = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.institutionName && values.risks.includes(riskTypeLife.death)) {
            existingErrors.institutionName = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.dateOfSeriousIllness && values.risks.includes(riskTypeLife.seriousIllness)) {
            existingErrors.dateOfSeriousIllness = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.diagnosisSeriousIllness && values.risks.includes(riskTypeLife.seriousIllness)) {
            existingErrors.diagnosisSeriousIllness = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        if (!values.seriousIllnessTreatment && values.risks.includes(riskTypeLife.seriousIllness)) {
            existingErrors.seriousIllnessTreatment = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }
        setErrors(existingErrors);

        if (valid) {
            goForward();
        }
    };

    const handleGoBack = (): void => {
        goBack();
    };

    useEffect(() => {
        if (values.hospitalDaysStart !== '') {
            const day = 60 * 60 * 24 * 1000;
            setFieldValue(
                'dayAfterHospitalDaysStart',
                formatDate(new Date(values.hospitalDaysStart.getTime() + day).toDateString()),
            );
        }
    }, [values.hospitalDaysStart]);

    const handleChangeDate = () => {
        setFieldValue('hospitalDaysEnd', null);
    };

    const canProceedPurchase = !values.validationData?.map((data: any) => data.validan).includes(false)
        ? handleNext
        : undefined;

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                    {translations.claimDetailsLife.DETAILS}
                </Typography>
                {firstCondition && (
                    <>
                        <FormControl>
                            <Typography id={'description-label'}>
                                {translations.claimDetailsLife.DESCRIPTION}
                            </Typography>
                            <Field
                                name="description"
                                multiline={true}
                                as={TextField}
                                maxRows={5}
                                minRows={5}
                                inputProps={{
                                    maxLength: maxCharacterLimit,
                                }}
                                value={values.description}
                                error={!!(touched.description && errors.description)}
                            />
                            {touched.description && errors.description ? (
                                <FormHelperText error={true}>
                                    <>{errors.description}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'typeOfInjury-label'}>
                                {translations.claimDetailsLife.TYPE_OF_INJURY}
                            </Typography>
                            <Field
                                name="typeOfInjury"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.typeOfInjury && errors.typeOfInjury)}
                            />
                            {touched.typeOfInjury && errors.typeOfInjury ? (
                                <FormHelperText error={true}>
                                    <>{errors.typeOfInjury}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'surgical-investigation-label'}>
                                {translations.claimDetailsLife.SURGICAL_INTERVENTION}
                            </Typography>
                            <Field
                                name="surgicalInvestigation"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.surgicalInvestigation && errors.surgicalInvestigation)}
                            />
                            {touched.surgicalInvestigation && errors.surgicalInvestigation ? (
                                <FormHelperText error={true}>
                                    <>{errors.surgicalInvestigation}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                            {translations.claimDetailsLife.HOSPITAL_DAYS}
                        </Typography>
                        <Typography>{translations.claimDetailsLife.HOSPITAL_DAYS_NOTE}</Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridGap: '15px',
                                gridTemplateColumns: window.innerWidth > 767 ? '1fr 1fr' : '1fr',
                            }}
                        >
                            <FormControl
                                error={!!(touched.hospitalDaysStart && errors.hospitalDaysStart)}
                                className="policyDatePicker"
                            >
                                <Field
                                    name="hospitalDaysStart"
                                    label={translations.claimDetailsLife.HOSPITAL_DAYS_FROM}
                                    as={DatePicker}
                                    maxDate={new Date(values.dateOfOccurance)}
                                    disableFuture
                                    onChangeDate={handleChangeDate}
                                    error={!!(touched.hospitalDaysStart && errors.hospitalDaysStart)}
                                />
                                {touched.hospitalDaysStart && errors.hospitalDaysStart ? (
                                    <FormHelperText error={true}>
                                        <>{errors.hospitalDaysStart}</>
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                            <FormControl
                                error={!!(touched.hospitalDaysEnd && errors.hospitalDaysEnd)}
                                className="policyDatePicker"
                            >
                                <Field
                                    name="hospitalDaysEnd"
                                    label={translations.claimDetailsLife.HOSPITAL_DAYS_TO}
                                    as={DatePicker}
                                    minDate={new Date(values.dayAfterHospitalDaysStart)}
                                    disableFuture
                                    error={!!(touched.hospitalDaysEnd && errors.hospitalDaysEnd)}
                                />
                                {touched.hospitalDaysEnd && errors.hospitalDaysEnd ? (
                                    <FormHelperText error={true}>
                                        <>{errors.hospitalDaysEnd}</>
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                        </Box>
                        <FormControl>
                            <Typography id={'investigation-label'}>
                                {translations.claimDetailsLife.INVESTIGATION}
                            </Typography>
                            <Field
                                name="investigation"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.investigation && errors.investigation)}
                            />
                            {touched.investigation && errors.investigation ? (
                                <FormHelperText error={true}>
                                    <>{errors.investigation}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'firstAid-label'}>{translations.claimDetailsLife.FIRST_AID}</Typography>
                            <Field
                                name="firstAid"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.firstAid && errors.firstAid)}
                            />
                            {touched.firstAid && errors.firstAid ? (
                                <FormHelperText error={true}>
                                    <>{errors.firstAid}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'physicalFlaw-label'}>
                                {translations.claimDetailsLife.PHYSICAL_FLAW}
                            </Typography>
                            <Field
                                name="physicalFlaw"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.physicalFlaw && errors.physicalFlaw)}
                            />
                            {touched.physicalFlaw && errors.physicalFlaw ? (
                                <FormHelperText error={true}>
                                    <>{errors.physicalFlaw}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'sameInjury-label'}>{translations.claimDetailsLife.SAME_INJURY}</Typography>
                            <Field
                                name="sameInjury"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.sameInjury && errors.sameInjury)}
                            />
                            {touched.sameInjury && errors.sameInjury ? (
                                <FormHelperText error={true}>
                                    <>{errors.sameInjury}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'prohibitedSubstances-label'}>
                                {translations.claimDetailsLife.PROHIBITED_SUBSTANCES}
                            </Typography>
                            <Field
                                name="prohibitedSubstances"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.prohibitedSubstances && errors.prohibitedSubstances)}
                            />
                            {touched.prohibitedSubstances && errors.prohibitedSubstances ? (
                                <FormHelperText error={true}>
                                    <>{errors.prohibitedSubstances}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.risks.includes(riskTypeLife.hospitalDaysDueToIllness) && (
                    <>
                        <FormControl>
                            <Typography id={'cause-label'}>{translations.claimDetailsLife.CAUSE}</Typography>
                            <Field
                                name="cause"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.cause && errors.cause)}
                            />
                            {touched.cause && errors.cause ? (
                                <FormHelperText error={true}>
                                    <>{errors.cause}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                            {translations.claimDetailsLife.HOSPITAL_DAYS}
                        </Typography>
                        <Typography>{translations.claimDetailsLife.HOSPITAL_DAYS_NOTE}</Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridGap: '15px',
                                gridTemplateColumns: window.innerWidth > 767 ? '1fr 1fr' : '1fr',
                            }}
                        >
                            <FormControl
                                error={!!(touched.hospitalDaysStart && errors.hospitalDaysStart)}
                                className="policyDatePicker"
                            >
                                <Field
                                    name="hospitalDaysStart"
                                    label={translations.claimDetailsLife.HOSPITAL_DAYS_FROM}
                                    as={DatePicker}
                                    maxDate={new Date(values.dateOfOccurance)}
                                    disableFuture
                                    onChangeDate={handleChangeDate}
                                    error={!!(touched.hospitalDaysStart && errors.hospitalDaysStart)}
                                />
                                {touched.hospitalDaysStart && errors.hospitalDaysStart ? (
                                    <FormHelperText error={true}>
                                        <>{errors.hospitalDaysStart}</>
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                            <FormControl
                                error={!!(touched.hospitalDaysEnd && errors.hospitalDaysEnd)}
                                className="policyDatePicker"
                            >
                                <Field
                                    name="hospitalDaysEnd"
                                    label={translations.claimDetailsLife.HOSPITAL_DAYS_TO}
                                    as={DatePicker}
                                    minDate={new Date(values.dayAfterHospitalDaysStart)}
                                    disableFuture
                                    error={!!(touched.hospitalDaysEnd && errors.hospitalDaysEnd)}
                                />
                                {touched.hospitalDaysEnd && errors.hospitalDaysEnd ? (
                                    <FormHelperText error={true}>
                                        <>{errors.hospitalDaysEnd}</>
                                    </FormHelperText>
                                ) : null}
                            </FormControl>
                        </Box>
                        <FormControl>
                            <Typography id={'hospitalType-label'}>
                                {translations.claimDetailsLife.HOSPITAL_TYPE}
                            </Typography>
                            <Field
                                name="hospitalType"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.hospitalType && errors.hospitalType)}
                            />
                            {touched.hospitalType && errors.hospitalType ? (
                                <FormHelperText error={true}>
                                    <>{errors.hospitalType}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl
                            error={!!(touched.firstContactDate && errors.firstContactDate)}
                            className="policyDatePicker"
                        >
                            <Typography id={'firstContactDate-label'}>
                                {translations.claimDetailsLife.FIRST_CONTACT_DATE}
                            </Typography>
                            <Field
                                name="firstContactDate"
                                as={DatePicker}
                                disableFuture
                                error={!!(touched.firstContactDate && errors.firstContactDate)}
                            />
                            {touched.firstContactDate && errors.firstContactDate ? (
                                <FormHelperText error={true}>
                                    <>{errors.firstContactDate}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'medicalRecord-label'}>
                                {translations.claimDetailsLife.MEDICAL_RECORD}
                            </Typography>
                            <Field
                                name="medicalRecord"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.medicalRecord && errors.medicalRecord)}
                            />
                            {touched.medicalRecord && errors.medicalRecord ? (
                                <FormHelperText error={true}>
                                    <>{errors.medicalRecord}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness) && (
                    <>
                        <FormControl>
                            <Typography id={'diagnosis-label'}>{translations.claimDetailsLife.DIAGNOSIS}</Typography>
                            <Field
                                name="diagnosis"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.diagnosis && errors.diagnosis)}
                            />
                            {touched.diagnosis && errors.diagnosis ? (
                                <FormHelperText error={true}>
                                    <>{errors.diagnosis}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'surgicalIntervention-label'}>
                                {translations.claimDetailsLife.NAME_SURGICAL_INTERVENTION}
                            </Typography>
                            <Field
                                name="surgicalIntervention"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.surgicalIntervention && errors.surgicalIntervention)}
                            />
                            {touched.surgicalIntervention && errors.surgicalIntervention ? (
                                <FormHelperText error={true}>
                                    <>{errors.surgicalIntervention}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'hospitalOfSurgical-label'}>
                                {translations.claimDetailsLife.HOSPITAL_OF_SURGICAL}
                            </Typography>
                            <Field
                                name="hospitalOfSurgical"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.hospitalOfSurgical && errors.hospitalOfSurgical)}
                            />
                            {touched.hospitalOfSurgical && errors.hospitalOfSurgical ? (
                                <FormHelperText error={true}>
                                    <>{errors.hospitalOfSurgical}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl
                            error={!!(touched.dateOfFirstContact && errors.dateOfFirstContact)}
                            className="policyDatePicker"
                        >
                            <Typography id={'dateOfFirstContact-label'}>
                                {translations.claimDetailsLife.DATE_OF_FIRST_CONTACT}
                            </Typography>
                            <Field
                                name="dateOfFirstContact"
                                as={DatePicker}
                                disableFuture
                                error={!!(touched.dateOfFirstContact && errors.dateOfFirstContact)}
                            />
                            {touched.dateOfFirstContact && errors.dateOfFirstContact ? (
                                <FormHelperText error={true}>
                                    <>{errors.dateOfFirstContact}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'medicalRecord-label'}>
                                {translations.claimDetailsLife.MEDICAL_RECORD}
                            </Typography>
                            <Field
                                name="medicalRecord"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.medicalRecord && errors.medicalRecord)}
                            />
                            {touched.medicalRecord && errors.medicalRecord ? (
                                <FormHelperText error={true}>
                                    <>{errors.medicalRecord}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.risks.includes(riskTypeLife.death) && (
                    <>
                        <FormControl>
                            <Typography id={'deathCause-label'}>{translations.claimDetailsLife.DEATH_CAUSE}</Typography>
                            <Field
                                name="deathCause"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.deathCause && errors.deathCause)}
                            />
                            {touched.deathCause && errors.deathCause ? (
                                <FormHelperText error={true}>
                                    <>{errors.deathCause}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'drName-label'}>{translations.claimDetailsLife.DR_NAME}</Typography>
                            <Field
                                name="drName"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.drName && errors.drName)}
                            />
                            {touched.drName && errors.drName ? (
                                <FormHelperText error={true}>
                                    <>{errors.drName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'institutionName-label'}>
                                {translations.claimDetailsLife.INSITUTION_NAME}
                            </Typography>
                            <Field
                                name="institutionName"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.institutionName && errors.institutionName)}
                            />
                            {touched.institutionName && errors.institutionName ? (
                                <FormHelperText error={true}>
                                    <>{errors.institutionName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {(values.risks.includes(riskTypeLife.deathByAccident) ||
                    values.risks.includes(riskTypeLife.deathByTrafficAccident)) && (
                    <>
                        <FormControl>
                            <Typography id={'description-label'}>
                                {translations.claimDetailsLife.CASE_DESCRIPTIOM}
                            </Typography>
                            <Field
                                name="description"
                                multiline={true}
                                as={TextField}
                                maxRows={5}
                                minRows={5}
                                inputProps={{
                                    maxLength: maxCharacterLimit,
                                }}
                                value={values.description}
                                error={!!(touched.description && errors.description)}
                            />
                            {touched.description && errors.description ? (
                                <FormHelperText error={true}>
                                    <>{errors.description}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'firstAid-label'}>{translations.claimDetailsLife.FIRST_AID}</Typography>
                            <Field
                                name="firstAid"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.firstAid && errors.firstAid)}
                            />
                            {touched.firstAid && errors.firstAid ? (
                                <FormHelperText error={true}>
                                    <>{errors.firstAid}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'investigation-label'}>
                                {translations.claimDetailsLife.INVESTIGATION}
                            </Typography>
                            <Field
                                name="investigation"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.investigation && errors.investigation)}
                            />
                            {touched.investigation && errors.investigation ? (
                                <FormHelperText error={true}>
                                    <>{errors.investigation}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'prohibitedSubstances-label'}>
                                {translations.claimDetailsLife.PROHIBITED_SUBSTANCES_DEATH}
                            </Typography>
                            <Field
                                name="prohibitedSubstances"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.prohibitedSubstances && errors.prohibitedSubstances)}
                            />
                            {touched.prohibitedSubstances && errors.prohibitedSubstances ? (
                                <FormHelperText error={true}>
                                    <>{errors.prohibitedSubstances}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.risks.includes(riskTypeLife.seriousIllness) && (
                    <>
                        <FormControl>
                            <Typography id={'dateOfSeriousIllness-label'}>
                                {translations.claimDetailsLife.DATE_SERIOUS_ILLNESS}
                            </Typography>
                            <Field
                                name="dateOfSeriousIllness"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.dateOfSeriousIllness && errors.dateOfSeriousIllness)}
                                required
                            />
                            {touched.dateOfSeriousIllness && errors.dateOfSeriousIllness ? (
                                <FormHelperText error={true}>
                                    <>{errors.dateOfSeriousIllness}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>

                        <FormControl>
                            <Typography id={'diagnosisSeriousIllness-label'}>
                                {translations.claimDetailsLife.DIAGNOSIS_SERIOUS_ILLNESS}
                            </Typography>
                            <Field
                                name="diagnosisSeriousIllness"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.diagnosisSeriousIllness && errors.diagnosisSeriousIllness)}
                                required
                            />
                            {touched.diagnosisSeriousIllness && errors.diagnosisSeriousIllness ? (
                                <FormHelperText error={true}>
                                    <>{errors.diagnosisSeriousIllness}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'seriousIllnessTreatment-label'}>
                                {translations.claimDetailsLife.SERIOUS_ILLNESS_TREATMENT}
                            </Typography>
                            <Field
                                name="seriousIllnessTreatment"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.seriousIllnessTreatment && errors.seriousIllnessTreatment)}
                                required
                            />
                            {touched.seriousIllnessTreatment && errors.seriousIllnessTreatment ? (
                                <FormHelperText error={true}>
                                    <>{errors.seriousIllnessTreatment}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Typography id={'medicalRecord-label'}>
                                {translations.claimDetailsLife.MEDICAL_RECORD}
                            </Typography>
                            <Field
                                name="medicalRecord"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={!!(touched.medicalRecord && errors.medicalRecord)}
                            />
                            {touched.medicalRecord && errors.medicalRecord ? (
                                <FormHelperText error={true}>
                                    <>{errors.medicalRecord}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
                {values.risks.includes(riskTypeLife.outlive) && (
                    <>
                        <Typography id={'seriousIllnessTreatment-label'}>
                            {values.capitalized ? (
                                <>
                                    {translations.claimDetailsLife.CAPITALIZED_LABEL}
                                    {formatDateSrb(values.dateCapitalized)}
                                </>
                            ) : (
                                <>
                                    {translations.claimDetailsLife.OUTLIVE_DEBT_AMOUNT_LABEL}
                                    {`${values.debtAmount} ${values.currency}`}
                                </>
                            )}
                        </Typography>
                        {values.calculationItems.length > 0 && (
                            <TableContainer component={Paper}>
                                <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>
                                                {translations.claimDetailsLife.OUTLIVE_TABLE_HEAD_COLUMN_1}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {translations.claimDetailsLife.OUTLIVE_TABLE_HEAD_COLUMN_2}
                                            </StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <>
                                            {values.calculationItems.map((row: GenericObject) => (
                                                <>
                                                    <StyledTableRow key={row.id ? row.id : row.opis}>
                                                        <StyledTableCell component="th" scope="row">
                                                            {row.opis}
                                                        </StyledTableCell>
                                                        <StyledTableCell component="th" scope="row">
                                                            {`${row.iznos} ${values.currency}`}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                </>
                                            ))}
                                            <StyledTableRow key={values.paymentAmount}>
                                                <StyledTableCell
                                                    component="th"
                                                    scope="row"
                                                    style={{ fontWeight: 'bold' }}
                                                >
                                                    {translations.claimDetailsLife.PAYMENT_AMOUNT}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    component="th"
                                                    scope="row"
                                                    style={{ fontWeight: 'bold' }}
                                                >
                                                    {`${values.paymentAmount} ${values.currency}`}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        </>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {values.capitalized ? null : (
                            <Typography id={'seriousIllnessTreatment-label'}>
                                {translations.claimDetailsLife.OUTLIVE_TEXT_LABEL_1}
                            </Typography>
                        )}
                        <Typography id={'seriousIllnessTreatment-label'}>
                            {translations.claimDetailsLife.OUTLIVE_TEXT_LABEL_2}
                        </Typography>
                        {values.tax && values.tax > 0 ? (
                            <>
                                <Typography id={'seriousIllnessTreatment-label'}>
                                    {translations.claimDetailsLife.OUTLIVE_TAX_LABEL_1}
                                    {`${values.taxBase} ${values.currency}`}
                                    {translations.claimDetailsLife.OUTLIVE_TAX_LABEL_2}
                                </Typography>
                                <Typography id={'seriousIllnessTreatment-label'}>
                                    {translations.claimDetailsLife.OUTLIVE_TAX_LABEL_3}
                                    {`${values.tax} ${values.currency}`}
                                </Typography>
                            </>
                        ) : null}
                        <Typography id={'seriousIllnessTreatment-label'}>
                            {translations.claimDetailsLife.OUTLIVE_POLICY_NUMBER}
                            {values.activePolicyNumber}
                            {translations.claimDetailsLife.OUTLIVE_CLAIM_NUMBER}
                            {values.claimCode}
                        </Typography>
                    </>
                )}
                {values.risks.includes(riskTypeLife.purchase) && (
                    <>
                        {values.validationData?.length > 0 && (
                            <TableContainer component={Paper}>
                                <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>
                                                {translations.claimDetailsLife.OUTLIVE_TABLE_HEAD_COLUMN_1}
                                            </StyledTableCell>
                                            <StyledTableCell>{''}</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <>
                                            {values.validationData.map((row: GenericObject) => (
                                                <>
                                                    <StyledTableRow key={row.id ? row.id : row.opis}>
                                                        <StyledTableCell component="th" scope="row">
                                                            {row.opis}
                                                        </StyledTableCell>
                                                        <StyledTableCell component="th" scope="row">
                                                            {row.validan === true ? (
                                                                <img
                                                                    style={{
                                                                        display: 'block',
                                                                        maxHeight: '40px',
                                                                        maxWidth: '40px',
                                                                        marginLeft: 'auto',
                                                                    }}
                                                                    src={svgChecked}
                                                                    alt="data-valid"
                                                                />
                                                            ) : (
                                                                <img
                                                                    style={{
                                                                        display: 'block',
                                                                        maxHeight: '40px',
                                                                        maxWidth: '40px',
                                                                        marginLeft: 'auto',
                                                                    }}
                                                                    src={svgError}
                                                                    alt="data-valid"
                                                                />
                                                            )}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                </>
                                            ))}
                                        </>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {values.paymentAmount === null && (
                            <Typography marginTop="15px">
                                {translations.claimDetailsLife.PURCHASE_PAYMENT_AMOUNT_NULL}
                            </Typography>
                        )}

                        {canProceedPurchase && values.calculationItems?.length > 0 && !!values.purchaseAmount && (
                            <>
                                <Typography color="#c21b17" marginTop="15px" fontWeight={600}>
                                    {translations.claimDetailsLife.PURCHASE_TEXT_NOTE}
                                </Typography>
                                <Typography color="#c21b17" fontWeight={600}>
                                    <>
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_1}
                                        {getCurrentDate()}
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_2}
                                        {values.purchaseValuePeriod}.
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_3}
                                        {`${values.purchaseAmount} ${values.currency}`}
                                    </>
                                </Typography>

                                <TableContainer component={Paper}>
                                    <Table sx={{ minWidth: 230 }} aria-label="customized table">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell>
                                                    {translations.claimDetailsLife.OUTLIVE_TABLE_HEAD_COLUMN_1}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    {translations.claimDetailsLife.OUTLIVE_TABLE_HEAD_COLUMN_2}
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            <>
                                                {values.calculationItems.map((row: GenericObject) => (
                                                    <>
                                                        <StyledTableRow key={row.id ? row.id : row.opis}>
                                                            <StyledTableCell component="th" scope="row">
                                                                {row.opis}
                                                            </StyledTableCell>
                                                            <StyledTableCell component="th" scope="row">
                                                                {`${row.iznos} ${values.currency}`}
                                                            </StyledTableCell>
                                                        </StyledTableRow>
                                                    </>
                                                ))}
                                                <StyledTableRow key={values.paymentAmount}>
                                                    <StyledTableCell
                                                        component="th"
                                                        scope="row"
                                                        style={{ fontWeight: 'bold' }}
                                                    >
                                                        {translations.claimDetailsLife.PAYMENT_AMOUNT}
                                                    </StyledTableCell>
                                                    <StyledTableCell
                                                        component="th"
                                                        scope="row"
                                                        style={{ fontWeight: 'bold' }}
                                                    >
                                                        {`${values.paymentAmount} ${values.currency}`}
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            </>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {values.tax && values.tax > 0 ? (
                                    <Typography>
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_4} <br></br>
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_5}
                                        {`${values.taxBase} ${values.currency}`}
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_6}
                                        <br></br>
                                        <br></br>
                                        {translations.claimDetailsLife.PURCHASE_TEXT_LABEL_7}
                                        {`${values.tax} ${values.currency}`}
                                    </Typography>
                                ) : null}
                            </>
                        )}
                    </>
                )}
            </StyledCardSection>
            <StepperControls
                onNext={values.risks.includes(riskTypeLife.purchase) ? canProceedPurchase : handleNext}
                onPrevious={handleGoBack}
                onNextLabel={common.NEXT}
                onPreviousLabel={common.PREVIOUS}
            />
        </StepWrapper>
    );
};

export const ClaimDetailsCleanup = (
    setFieldValue: (field: string, value: string | number, shouldValidate?: boolean | undefined) => void,
) => {
    setFieldValue('description', '');
    setFieldValue('typeOfInjury', '');
    setFieldValue('investigation', '');
    setFieldValue('surgicalInvestigation', '');
    setFieldValue('hospitalDaysStart', '');
    setFieldValue('hospitalDaysEnd', '');
    setFieldValue('firstAid', '');
    setFieldValue('physicalFlaw', '');
    setFieldValue('sameInjury', '');
    setFieldValue('hospitalType', '');
    setFieldValue('firstContactDate', '');
    setFieldValue('medicalRecord', '');
    setFieldValue('diagnosis', '');
    setFieldValue('surgicalIntervention', '');
    setFieldValue('prohibitedSubstances', '');
    setFieldValue('hospitalOfSurgical', '');
    setFieldValue('dateOfFirstContact', '');
    setFieldValue('cause', '');
    setFieldValue('deathCause', '');
    setFieldValue('drName', '');
    setFieldValue('institutionName', '');
    setFieldValue('dateOfSeriousIllness', '');
    setFieldValue('diagnosisSeriousIllness', '');
    setFieldValue('seriousIllnessTreatment', '');
};

export default ClaimDetailScreen;
